import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Dispatch, FormEvent, SetStateAction } from "react";
import { useState } from "react";
import { useCardInfo, useToast } from "app/hooks";
import { logError } from "@lib/logger";
import { StripeCardNumberElementOptions, TokenResult } from "@stripe/stripe-js";

import "styles/payment.css";
import { GLOBALS } from "config";

const stripeElementOpt: StripeCardNumberElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      "::placeholder": {
        color: "#CCC",
      },
    },
  },
  disableLink: true,
};

/**
 * Stripeへカード情報登録・csvトークン作成などを行うtrigger
 */
export const paymentTrigger: {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  triggerSubmit: ((e?: any) => Promise<TokenResult | void>) | undefined;
} = {
  triggerSubmit: undefined,
};

const Payment = ({
  inputOnly,
  cvcOnly,
  customerCd,
  setIsEditing,
}: {
  inputOnly?: boolean;
  cvcOnly?: boolean;
  customerCd?: string;
  setIsEditing?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { pushToast } = useToast();

  const stripe = useStripe();
  const elements = useElements();
  const { mutate } = useCardInfo();
  const [isFetching, setIsFetching] = useState(false);
  const [cardNumberErrorMessage, setCardNumberErrorMessage] = useState<string>("");
  const [cardExpiryErrorMessage, setCardExpiryErrorMessage] = useState<string>("");
  const [cardCvcErrorMessage, setCardCvcErrorMessage] = useState<string>("");

  const getCvcTokenResult = async () => {
    if (!stripe || !elements) return Promise.reject();
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement === null) return Promise.reject();
    const result = await stripe.createToken("cvc_update", cvcElement);
    return result;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }
    if (!stripe || !elements) return Promise.reject();

    if (cvcOnly) {
      // cvc入力のみの場合はcvc_tokenを取得して呼び出し元へ返す
      return await getCvcTokenResult();
    }
    // それ以外のカード情報入力時は、stripeCustomerの登録から

    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement === null) return Promise.reject();

    setIsFetching(true);

    const result = await stripe.createToken(cardElement);
    if (!result.token?.card?.id) {
      pushToast("カード情報が正しく入力されていないようです", { type: "error" });
      setIsFetching(false);
      return Promise.reject();
    }
    return fetch("/api/checkout/striperegister", {
      method: "POST",
      body: JSON.stringify({
        customerCd: customerCd || "",
        cardToken: result.token.card.id,
        stripeToken: result.token.id,
      }),
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const json = await res.json();
          if (json.message) {
            throw new Error(JSON.stringify(json.message));
          } else {
            throw new Error();
          }
        }
        if (!inputOnly) {
          pushToast("カード情報を登録しました");
          if (setIsEditing !== undefined) setIsEditing(false);
          mutate();
        } else {
          // inputOnlyの場合はそのままcvc_tokenを取得して呼び出し元へ返す
          return await getCvcTokenResult();
        }
      })
      .catch((e) => {
        logError("server error: stripe register");
        const message = JSON.parse(e.message);
        if (message && message !== "error") {
          pushToast(message, {
            type: "error",
            livingSec: 8,
            isStripeError: true,
          });
        } else {
          pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
            type: "error",
          });
        }
        return Promise.reject();
      })
      .finally(() => {
        setIsFetching(false);
      });
  };
  paymentTrigger.triggerSubmit = handleSubmit;
  return (
    <form onSubmit={(e) => handleSubmit(e)} className="form">
      {!cvcOnly && (
        <div className="form-item mode-required">
          <label className="form-label">
            <span className="form-label-text">カード番号</span>
            <span>
              <span className="payment-input-with-caption">
                <span className="form-input">
                  <span className="form-input-text">
                    <CardNumberElement
                      options={{ ...stripeElementOpt, placeholder: "16桁数字のカード番号" }}
                      onChange={(event) => {
                        if (event.error) {
                          setCardNumberErrorMessage("無効なカード番号です。");
                        } else {
                          setCardNumberErrorMessage("");
                        }
                      }}
                    />
                  </span>
                </span>
                <span className="payment-caption">
                  ※対応ブランドは
                  <a
                    href={GLOBALS.HELP_URL + GLOBALS.HELP_PATH_SUPPORTED_CARD_BRAND}
                    className="payment-caption-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    こちら
                  </a>
                </span>
              </span>
              {cardNumberErrorMessage && (
                <span className="payment-error-message">{cardNumberErrorMessage}</span>
              )}
            </span>
          </label>
        </div>
      )}
      {!cvcOnly && (
        <div className="form-item mode-required">
          <label className="form-label">
            <span className="form-label-text">有効期限</span>
            <span className="form-input">
              <span className="form-input-text mode-s">
                <CardExpiryElement
                  options={{ ...stripeElementOpt, placeholder: "月 / 年 (MM / YY)" }}
                  onChange={(event) => {
                    if (event.error) {
                      setCardExpiryErrorMessage("カードの有効期限が範囲外です。");
                    } else {
                      setCardExpiryErrorMessage("");
                    }
                  }}
                />
              </span>

              {cardExpiryErrorMessage && (
                <span className="payment-error-message">{cardExpiryErrorMessage}</span>
              )}
            </span>
          </label>
        </div>
      )}
      <div className="form-item mode-required">
        <label className="form-label">
          <span className="form-label-text">セキュリティコード</span>
          <span className="form-input">
            <span className="form-input-text mode-s">
              <CardCvcElement
                options={{ ...stripeElementOpt, placeholder: "000" }}
                onChange={(event) => {
                  if (event.error) {
                    setCardCvcErrorMessage("確認コード(CVC)が不正です。");
                  } else {
                    setCardCvcErrorMessage("");
                  }
                }}
              />
            </span>
            {cardCvcErrorMessage && (
              <span className="payment-error-message">{cardCvcErrorMessage}</span>
            )}
          </span>
        </label>
      </div>
      {!inputOnly && (
        <div className="form-item">
          <div className="form-label">
            <span className="form-label-text mode-empty"></span>
            <span className="form-input mode-submit">
              <button className="form-btn mode-submit" type="submit" disabled={isFetching}>
                カードを登録する
              </button>
            </span>
          </div>
        </div>
      )}
    </form>
  );
};

export default Payment;
